import React from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileSignature, FileDown, ShieldCheck, Clock, MessageSquare } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { t, formatMoney } from "@/i18n";
import TranslateButton from "@/components/TranslateButton";

/** One row in the intermediation contracts list. */
export default function IntermediationRow({ contract, isBuyer, onDownload, onOpenChat, onSign }) {
  const { lang } = useApp();
  const canSign = isBuyer && !contract.buyer_signed;
  const bilateral = !!contract.buyer_signed;
  const counterparty = isBuyer
    ? `${t("agent", lang)}: ${contract.agent_name}`
    : `${t("buyer", lang)}: ${contract.buyer_name}`;
  return (
    <div
      data-testid={`intermediation-${contract.id}`}
      className={`rounded-md border p-4 ${
        canSign ? "border-[#C5A880] bg-gradient-to-r from-[#F4EFE6] to-[#FFFAF0]" : "border-[#D8CFBB] bg-[#F5F0E6]"
      }`}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="text-sm text-[#1A1610] font-medium truncate">{contract.property_title}</p>
          <p className="text-xs text-[#6B5F4C]">
            {contract.property_city} · {formatMoney(contract.property_price, lang)}
          </p>
          <p className="text-[11px] text-[#7A6E5A] mt-1">
            {counterparty} · {new Date(contract.created_at).toLocaleDateString()}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {bilateral && (
            <Badge className="bg-emerald-700 text-white text-[10px]">
              <ShieldCheck className="h-3 w-3 me-1" /> {t("interm_bilateral", lang)}
            </Badge>
          )}
          {!bilateral && (
            <Badge className="bg-amber-600 text-white text-[10px]">
              <Clock className="h-3 w-3 me-1" /> {t("interm_pending", lang)}
            </Badge>
          )}
          <Button
            size="sm"
            onClick={() => onDownload(contract)}
            data-testid={`interm-pdf-${contract.id}`}
            className="bg-transparent border border-[#C5A880]/50 text-[#C5A880] hover:bg-[#C5A880]/10 rounded-full"
          >
            <FileDown className="h-3.5 w-3.5" />
          </Button>
          <TranslateButton kind="intermediation" id={contract.id} />
          {bilateral && (
            <Button
              size="sm"
              onClick={() => onOpenChat(contract)}
              data-testid={`interm-chat-${contract.id}`}
              className="bg-emerald-700 text-white hover:bg-emerald-800 rounded-full"
            >
              <MessageSquare className="h-3.5 w-3.5 me-1.5" />
              {t("chat", lang)}
            </Button>
          )}
          {canSign && (
            <Button
              size="sm"
              onClick={() => onSign(contract)}
              data-testid={`interm-sign-${contract.id}`}
              className="bg-[#1A1610] text-[#F5F0E6] hover:bg-[#2E2A22] rounded-full"
            >
              <FileSignature className="h-3.5 w-3.5 me-1.5" />
              {t("sign_verb", lang)}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
